/**
 * `wayai bases report …` — the reporter-facing side of the DATA triage queue.
 *
 * Namespaced not because `report` collides in wording but because there are
 * still TWO queues: `wayai report …` files to WayAI's, this files to the Data
 * one. They collapse into a single command when the queues themselves collapse,
 * and not before (docs/reference/rekor-platform-contracts.md §3.2).
 */

import { Command } from 'commander';
import { readFileSync } from 'node:fs';
import { dirname, join } from 'node:path';
import { fileURLToPath } from 'node:url';
import { createDataClient } from '../client.js';
import { printOutput } from '../output.js';
import { findBase, outputFormat, pathSegment } from '../helpers.js';
import { ApiError } from '../../lib/api-client.js';
import { sanitizeTerminalText } from '../../lib/terminal-output.js';
import { findGitRoot } from '../../lib/workspace.js';
import { findInstalledSkills, lowestInstalledVersion } from '../../lib/skill-version.js';

interface ReportResponse {
  report_id: string;
  created: boolean;
  fingerprint: string;
  recurrence?: { reopened: boolean };
}

/**
 * Reporter-facing projection: the backend deliberately withholds the triage
 * fields (the linked issue, the AI verdict) from this view, so the CLI models
 * only what a reporter may see.
 */
interface ReporterSummary {
  report_id: string;
  title?: string | null;
  description?: string | null;
  classification: string | null;
  status: string;
  created_at: string;
  triaged_at: string | null;
  resolved_at: string | null;
  contest_count: number;
  dismissal_final: boolean;
}

interface ReportMessage {
  message_id: string;
  author_role: string;
  transition: string | null;
  body: string;
  created_at: string;
}

const here = dirname(fileURLToPath(import.meta.url));

/**
 * The running CLI version, best-effort.
 *
 * Two candidates because the module sits at a different depth once bundled: the
 * shipped binary is one file in `dist/`, while tests and typechecks load this
 * from `src/data/commands/`. A miss is not an error — the stamp is provenance
 * for triage, and it must never be the reason a report fails to file.
 */
function resolveCliVersion(): string {
  for (const candidate of [
    join(here, '..', 'package.json'),
    join(here, '..', '..', '..', 'package.json'),
  ]) {
    try {
      const version = (JSON.parse(readFileSync(candidate, 'utf-8')) as { version?: unknown })
        .version;
      if (typeof version === 'string' && version) return version;
    } catch {
      // Try the next candidate.
    }
  }
  return 'unknown';
}

/**
 * Client provenance stamped onto every `report create` — the running CLI version
 * and the lowest installed skill version (the same "lowest wins" rule the update
 * nudge uses, so the stamped version matches what the agent is actually
 * running). Lets triage close reports already fixed in a newer release instead of
 * re-investigating. Skill detection is best-effort: a filesystem hiccup or an
 * uninstalled skill just omits `skill_version` — it never blocks the report.
 */
export function resolveReportClientVersions(
  projectRoot: string,
  cliVersion: string,
): { cli_version: string; skill_version?: string } {
  const versions: { cli_version: string; skill_version?: string } = { cli_version: cliVersion };
  try {
    // `lowestInstalledVersion` returns null for an empty/version-less set, so no
    // length guard is needed.
    const skillVersion = lowestInstalledVersion(findInstalledSkills(projectRoot));
    if (skillVersion) versions.skill_version = skillVersion;
  } catch {
    // Best-effort — never block a report on skill-version detection.
  }
  return versions;
}

/** Map the reporter verbs' failures to friendly one-liners; re-throw anything else. */
function reportActionError(err: unknown): never {
  if (err instanceof ApiError) {
    if (err.status === 404) {
      console.error('Report not found, or it was not filed by you.');
    } else if (err.status === 403) {
      console.error('This report is agent-authored and can only be handled by a platform admin.');
    } else if (err.status === 409) {
      // A conflict carries the actionable reason (wrong status, final dismissal,
      // cap reached).
      console.error(err.message);
    } else if (err.status === 429) {
      console.error('Rate limit exceeded. Please try again later.');
    } else {
      console.error(`Request failed: ${err.message}`);
    }
    return process.exit(1);
  }
  throw err;
}

export function buildBasesReportCommand(): Command {
  const report = new Command('report').description(
    'File a report to the Data triage queue and verify the outcome',
  );

  report
    .command('create')
    .description('Submit a report to the Data triage queue (deduplicated)')
    .requiredOption('--title <title>', 'Short summary')
    .requiredOption('--description <text>', 'What happened')
    .option('--source <source>', 'Origin: cli_report (default) | review | security_audit')
    .option('--classification <c>', 'Nature: bug (default) | flaky_test | security | enhancement')
    .option(
      '--dedup-key <key>',
      'Stable dedup key (e.g. "<file>::<test>"); collapses repeat occurrences',
    )
    .option('--severity <level>', 'low | medium | high | critical')
    .option('--steps <text>', 'Steps to reproduce')
    .option('--error-message <text>', 'Exact error text if any')
    .option('--context <text>', 'Additional context (logs, request ids)')
    .option('--locale <code>', 'Notification locale (en | pt | es)', 'en')
    .option('--reporter-email <addr>', 'Override reporter email (defaults to session email)')
    // Entity pointers: which base/record type/record the error relates to.
    // Carried so triage can investigate the live data.
    .option('--base-id <id>', 'Base the error relates to (defaults to the configured base)')
    .option('--record-type <name>', 'Record type the error relates to')
    .option('--record-id <id>', 'Record id (UUID) the error relates to')
    .option('--external-id <id>', 'External id of the related record')
    .option('--external-source <src>', 'External source of the related record')
    .option('--relationship-id <id>', 'Relationship id the error relates to')
    .action(async function (
      this: Command,
      opts: {
        title: string;
        description: string;
        source?: string;
        classification?: string;
        dedupKey?: string;
        severity?: string;
        steps?: string;
        errorMessage?: string;
        context?: string;
        locale?: string;
        reporterEmail?: string;
        baseId?: string;
        recordType?: string;
        recordId?: string;
        externalId?: string;
        externalSource?: string;
        relationshipId?: string;
      },
    ) {
      const body: Record<string, unknown> = {
        title: opts.title,
        description: opts.description,
        locale: opts.locale,
      };
      if (opts.source) body.source = opts.source;
      if (opts.classification) body.classification = opts.classification;
      if (opts.dedupKey) body.dedup_key = opts.dedupKey;
      if (opts.severity) body.severity = opts.severity;
      if (opts.steps) body.steps = opts.steps;
      if (opts.errorMessage) body.error_message = opts.errorMessage;
      if (opts.context) body.context = opts.context;
      if (opts.reporterEmail) body.reporter_email = opts.reporterEmail;

      // Entity pointers. Default the base from the working session's own
      // selection so a report filed mid-task carries enough to investigate.
      // Optional here, so it must NOT go through `requireBase` — that exits 1.
      const baseId = opts.baseId ?? findBase(this) ?? process.env.WAYAI_BASE;
      if (baseId) body.base_id = baseId;
      if (opts.recordType) body.record_type = opts.recordType;
      if (opts.recordId) body.record_id = opts.recordId;
      if (opts.externalId) body.external_id = opts.externalId;
      if (opts.externalSource) body.external_source = opts.externalSource;
      if (opts.relationshipId) body.relationship_id = opts.relationshipId;

      // Stamp the CLI + installed-skill versions so triage can spot
      // stale-version reports.
      const versions = resolveReportClientVersions(
        findGitRoot() ?? process.cwd(),
        resolveCliVersion(),
      );
      body.cli_version = versions.cli_version;
      if (versions.skill_version) body.skill_version = versions.skill_version;

      const client = await createDataClient();
      const data = await client.request<ReportResponse>('POST', '/v1/reports', body);
      // Honors --json: the dedup outcome (created / reopened / merged) is
      // exactly what a filing script branches on, and it is in the response.
      if (outputFormat(this) === 'json') {
        printOutput(data, 'json');
        return;
      }
      if (data.created) {
        console.log(`Report submitted (id: ${data.report_id}).`);
        console.log('Our team will review and follow up by email if you provided one.');
      } else if (data.recurrence?.reopened) {
        console.log(`Report reopened for another look (id: ${data.report_id}).`);
      } else if (data.recurrence) {
        console.log(
          `A matching report remains closed (id: ${data.report_id}); this occurrence was recorded.`,
        );
      } else {
        console.log(
          `A matching report already exists (id: ${data.report_id}) — your submission was merged into it.`,
        );
      }
    });

  report
    .command('list')
    .description('List your reports, newest first')
    .option('--status <s>', 'Filter by status (e.g. shipped — awaiting your verification)')
    .action(async function (this: Command, opts: { status?: string }) {
      const client = await createDataClient();
      const qs = opts.status ? `?status=${encodeURIComponent(opts.status)}` : '';
      try {
        const rows = await client.request<ReporterSummary[]>('GET', `/v1/reports${qs}`);
        if (outputFormat(this) === 'json') {
          printOutput(rows, 'json');
          return;
        }
        if (rows.length === 0) {
          console.log('No reports found.');
          return;
        }
        for (const r of rows) {
          // The title is reporter-authored free text and this line is built by
          // hand rather than passed through `printOutput`, so it gets the
          // sanitizer explicitly — a control sequence in one title would
          // otherwise rewrite the rest of the listing.
          const title = sanitizeTerminalText(r.title?.replace(/\s+/g, ' ').trim() || '(untitled)');
          console.log(
            `${r.report_id}  ${r.status.padEnd(10)}  ${(r.classification ?? 'bug').padEnd(12)}  ${title}`,
          );
        }
      } catch (err) {
        reportActionError(err);
      }
    });

  report
    .command('get <report_id>')
    .description('Show one of your reports: status + the message thread')
    .action(async function (this: Command, reportId: string) {
      const client = await createDataClient();
      try {
        const data = await client.request<{
          report: ReporterSummary;
          messages: ReportMessage[];
        }>('GET', `/v1/reports/${pathSegment(reportId, 'report id')}`);
        if (outputFormat(this) === 'json') {
          printOutput(data, 'json');
          return;
        }
        const { report: row, messages } = data;
        // Title, description and every thread body are free text authored by the
        // reporter or by triage. This view builds its lines by hand, so each one
        // is sanitized here rather than relying on `printOutput`.
        console.log(`Report ${row.report_id}`);
        console.log(`  title:          ${sanitizeTerminalText(row.title ?? '(unavailable)')}`);
        console.log(
          `  description:    ${sanitizeTerminalText(row.description ?? '(unavailable)')}`,
        );
        console.log(`  status:         ${row.status}`);
        console.log(`  classification: ${row.classification ?? 'bug'}`);
        if (row.contest_count > 0) console.log(`  contests:       ${row.contest_count}`);
        if (row.dismissal_final) console.log('  dismissal:      final (non-contestable)');
        if (messages.length > 0) {
          console.log('\n  Thread:');
          for (const m of messages) {
            console.log(`    [${m.author_role}] ${sanitizeTerminalText(m.body)}`);
          }
        }
        if (row.status === 'shipped') {
          console.log(
            `\n  This fix is shipped. Run \`wayai bases report accept ${row.report_id}\` if it works,`,
          );
          console.log(
            `  or \`wayai bases report contest ${row.report_id} --reason "..."\` if it does not.`,
          );
        }
      } catch (err) {
        reportActionError(err);
      }
    });

  report
    .command('accept <report_id>')
    .description('Accept a shipped fix (→ addressed)')
    .action(async function (this: Command, reportId: string) {
      const client = await createDataClient();
      try {
        const data = await client.request<{ report: ReporterSummary }>(
          'POST',
          `/v1/reports/${pathSegment(reportId, 'report id')}/accept`,
        );
        if (outputFormat(this) === 'json') {
          printOutput(data, 'json');
          return;
        }
        console.log(`Accepted. Report ${data.report.report_id} → ${data.report.status}.`);
      } catch (err) {
        reportActionError(err);
      }
    });

  report
    .command('contest <report_id>')
    .description('Contest a shipped fix or a dismissal (→ back to triage)')
    .requiredOption('--reason <text>', 'Why the fix/dismissal is wrong')
    .action(async function (this: Command, reportId: string, opts: { reason: string }) {
      const client = await createDataClient();
      try {
        const data = await client.request<{ report: ReporterSummary }>(
          'POST',
          `/v1/reports/${pathSegment(reportId, 'report id')}/contest`,
          { reason: opts.reason },
        );
        if (outputFormat(this) === 'json') {
          printOutput(data, 'json');
          return;
        }
        console.log(
          `Contested. Report ${data.report.report_id} → ${data.report.status} (back to triage).`,
        );
      } catch (err) {
        reportActionError(err);
      }
    });

  return report;
}
